"use client"

import { LeafletShare } from "@/components/leaflet/LeafletShare"
import { LeafletSidebar } from "@/components/leaflet/LeafletSidebar"
import { LeafletItem, WeeklyLeafletContentProps } from "@/components/leaflet/types"
import {
  calculateStatus,
  handleCopyLink as copyLink,
  handleDownload as downloadLeaflet,
} from "@/components/leaflet/utils"
import { useLeaflets } from "@/hook/useLeaflets"
import { getFixedT } from "@/languages/i18n"
import Link from "next/link"
import { useEffect, useMemo, useState } from "react"
import { FilterButtons, type FilterValue } from "./FilterButtons"
import { LeafletCard } from "./LeafletCard"
import { LeafletSkeleton } from "./LeafletSkeleton"
import { LetakResponseDataObject } from "@/types/codegen/data-contracts"

export type { LeafletItem, WeeklyLeafletContentProps } from "@/components/leaflet/types"

export const WeeklyLeafletContent = ({
  title,
  locale = "vi",
  className = "",
  isDetailPage = false,
  currentItem = null,
  viewAllUrl = "/",
  isDisplayLink = true,
}: WeeklyLeafletContentProps) => {
  const [filter, setFilter] = useState<FilterValue>("all")
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState<LetakResponseDataObject | null>(null)

  const { leafletItems, loading } = useLeaflets({ locale  })

  const t = useMemo(() => getFixedT(locale), [locale])

  const filters: Array<{ id: FilterValue; label: string }> = useMemo(
    () => [
      { id: "all", label: t("All") },
      { id: "ongoing", label: t("Đang diễn ra") },
      { id: "upcoming", label: t("Upcoming") },
      { id: "expired", label: t("Expired") },
    ],
    [t],
  )

  const filteredItems = useMemo(() => {
    return leafletItems
      .map(item => ({
        ...item,
        status: calculateStatus(item.dateStart, item.dateEnd), // Tính lại status từ dateStart và dateEnd
      }))
      .filter(item => {
        if (filter === "all") return true
        return item.status === filter
      })
  }, [leafletItems, filter])

  // Handle fullscreen
  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement
        .requestFullscreen()
        .then(() => {
          setIsFullscreen(true)
        })
        .catch(err => {
          console.error("Error attempting to enable fullscreen:", err)
        })
    } else {
      document
        .exitFullscreen()
        .then(() => {
          setIsFullscreen(false)
        })
        .catch(err => {
          console.error("Error attempting to exit fullscreen:", err)
        })
    }
  }

  // Handle download
  const handleDownload = () => {
    downloadLeaflet(currentItem)
  }

  // Handle share
  const handleShare = () => {
    if (!currentItem) return
    setSelectedItem(currentItem)
    setShowShareModal(true)
  }

  // Copy link to clipboard
  const handleCopyLink = () => {
    copyLink(
      selectedItem,
      locale,
      () => {
        alert(t("Copied to clipboard"))
      },
      err => {
        console.error(t("Failed to copy link"), err)
      },
    )
  }

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener("fullscreenchange", handleFullscreenChange)
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange)
    }
  }, [])

  return (
    <div className={`w-full flex gap-0 ${className}`}>
      {/* Left Sidebar - Only show on detail page */}
      {isDetailPage && (
        <LeafletSidebar
          locale={locale}
          isFullscreen={isFullscreen}
          showShareModal={showShareModal}
          currentItem={currentItem as any}
          onFullscreen={handleFullscreen}
          onDownload={handleDownload}
          onShare={handleShare}
          variant="default"
        />
      )}

      {/* Main Content */}
      <div className="flex flex-col gap-3 w-full max-w-[1304px] mx-auto sm:px-2 px-4 sm:pt-16 pt-[28px] pb-6">
        <div className="flex justify-between sm:items-center">
          <div className="flex sm:flex-row flex-col sm:gap-2.5 gap-[2px] overflow-hidden">
            <div className="flex justify-between items-center">
              <h2 className="sm:text-2xl sm:leading-[37.2px] text-lg leading-[27.9px] font-bold uppercase text-[#333]">
                {title}
              </h2>
              {isDisplayLink && (
                <Link
                  href={viewAllUrl}
                  className="sm:hidden block sm:text-base sm:leading-[24.8px] text-sm leading-[21.7px] font-normal text-[#19B04E] hover:text-none"
                >
                  {t("View All")}
                </Link>
              )}
            </div>
            <FilterButtons filters={filters} currentFilter={filter} onFilterChange={setFilter} />
          </div>
          {isDisplayLink && (
            <Link
              href={viewAllUrl}
              className="text-base leading-[24.8px] font-normal text-[#19B04E] hover:text-none hidden sm:block"
            >
              {t("View All")}
            </Link>
          )}
        </div>

        {loading ? (
          <LeafletSkeleton count={2} />
        ) : (
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filteredItems.map((item, index) => (
              <LeafletCard key={item.id || index} item={item} locale={locale} index={index} />
            ))}
            {filteredItems.length === 0 && (
              <div className="col-span-full text-center py-10 text-gray-500">{t("No data available")}</div>
            )}
          </div>
        )}
      </div>

      {/* Share Modal */}
      {showShareModal && selectedItem && (
        <LeafletShare
          item={selectedItem}
          locale={locale}
          onCopyLink={handleCopyLink}
          variant="modal"
          show={showShareModal}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  )
}
